// =============================================
// Admin Staff Management Page
// Kipikid - מערכת נוכחות ילדים בגנים
// Enhanced: Real API, expanded roles, per-day kindergarten assignment
// =============================================

import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus,
  Search,
  User,
  Phone,
  Mail,
  Building2,
  Camera,
  Upload,
  X,
  ChevronLeft,
  ChevronRight,
  Edit2,
  Trash2,
  Clock,
  CheckCircle2,
  Image as ImageIcon,
  AlertCircle,
} from 'lucide-react';
import {
  adminApi,
  type AdminStaffDto,
  type AdminKindergartenSearchDto,
  type RoleTypeDto,
  type CreateStaffRequest,
} from '../../services/api';
import { useAuthStore } from '../../stores/authStore';
import { useAdminStore } from '../../stores/adminStore';
import FormLabel from '../../components/common/FormLabel';
import HelperText from '../../components/common/HelperText';
import { adminStaffHelpTexts } from '../../constants/helpTexts';
import { adminColors as colors } from '../../styles/colors';

const daysOfWeek = [
  { value: 1, label: 'א', fullLabel: 'ראשון' },
  { value: 2, label: 'ב', fullLabel: 'שני' },
  { value: 3, label: 'ג', fullLabel: 'שלישי' },
  { value: 4, label: 'ד', fullLabel: 'רביעי' },
  { value: 5, label: 'ה', fullLabel: 'חמישי' },
  { value: 6, label: 'ו', fullLabel: 'שישי' },
];

const roleColors: Record<number, { bg: string; fg: string }> = {
  4: { bg: colors.primaryLight, fg: colors.primary },     // גננת
  5: { bg: colors.secondaryLight, fg: colors.secondary },  // סייעת
  6: { bg: '#fef3c7', fg: '#d97706' },                    // מנהל
  7: { bg: '#e0e7ff', fg: '#4338ca' },                    // מוקד
  8: { bg: '#fce7f3', fg: '#db2777' },                    // מנהל עירייה
  9: { bg: '#ccfbf1', fg: '#0d9488' },                    // מנהל רשת
  10: { bg: '#fef9c3', fg: '#ca8a04' },                   // מפקח
  11: { bg: '#e2e8f0', fg: '#475569' },                   // טכנאי
};

// תפקידי ניהול — לא משויכים לגן, מדלגים על Step 2
const MANAGEMENT_ROLES = [6, 7, 8, 9, 10, 11]; // Admin, CallCenter, CityManager, NetworkManager, Inspector, Technician

export default function AdminStaff() {
  const { user } = useAuthStore();
  const { selectedOrganizationId } = useAdminStore();
  const orgFilter = user?.organizationPublicId || selectedOrganizationId || undefined;
  const [staff, setStaff] = useState<AdminStaffDto[]>([]);
  const [roleTypes, setRoleTypes] = useState<RoleTypeDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState<AdminStaffDto | null>(null);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const staffResult = await adminApi.getStaff(orgFilter);
        if (staffResult.success && staffResult.data) setStaff(staffResult.data);
      } catch (err) {
        console.error('Failed to load staff data:', err);
      } finally {
        setLoading(false);
      }
      // טעינת תפקידים בנפרד - לא חוסם את רשימת העובדים
      try {
        const roleResult = await adminApi.getRoleTypes();
        if (roleResult.success && roleResult.data) setRoleTypes(roleResult.data);
      } catch (err) {
        console.error('Failed to load role types:', err);
      }
    };
    loadData();
  }, [orgFilter]);

  const filteredStaff = staff.filter(s => {
    const fullName = `${s.firstName} ${s.lastName}`;
    const q = searchQuery.trim();
    if (!q) return true;
    return fullName.includes(q) ||
           s.phone?.includes(q) ||
           s.roleName.includes(q) ||
           s.dayAssignments.some(d => d.kindergartenName.includes(q));
  });

  const handleDelete = async (publicId: string) => {
    try {
      const result = await adminApi.deleteStaff(publicId);
      if (result.success) {
        setStaff(prev => prev.filter(s => s.publicId !== publicId));
        setSelectedStaff(null);
        setSuccessMessage('העובד נמחק בהצלחה');
        setTimeout(() => setSuccessMessage(''), 3000);
      }
    } catch (err) {
      console.error('Failed to delete staff:', err);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: colors.text, marginBottom: '4px' }}>
            ניהול עובדים
          </h1>
          <p style={{ fontSize: '14px', color: colors.textLight }}>
            {staff.length} עובדים רשומים במערכת
          </p>
        </div>
        <motion.button
          onClick={() => setShowAddModal(true)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 20px',
            backgroundColor: colors.primary,
            color: 'white',
            border: 'none',
            borderRadius: '12px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
          }}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <Plus size={18} />
          הוסף עובד
        </motion.button>
      </div>

      {/* Success Message */}
      <AnimatePresence>
        {successMessage && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 16px',
              backgroundColor: colors.successLight,
              borderRadius: '10px',
            }}
          >
            <CheckCircle2 size={18} color={colors.success} />
            <span style={{ fontSize: '14px', color: colors.success }}>{successMessage}</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Search */}
      <div style={{ position: 'relative' }}>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="חיפוש לפי שם, טלפון, תפקיד או גן..."
          style={{
            width: '100%',
            padding: '14px 44px 14px 14px',
            fontSize: '15px',
            border: `2px solid ${colors.border}`,
            borderRadius: '12px',
            outline: 'none',
            boxSizing: 'border-box',
            direction: 'rtl',
          }}
        />
        <Search
          size={20}
          color={colors.textLight}
          style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)' }}
        />
      </div>

      {/* Staff List */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: colors.textLight }}>
          טוען עובדים...
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {filteredStaff.map((member, index) => (
            <StaffCard
              key={member.publicId}
              member={member}
              index={index}
              onClick={() => setSelectedStaff(member)}
            />
          ))}
          {filteredStaff.length === 0 && (
            <div style={{
              textAlign: 'center',
              padding: '40px',
              backgroundColor: 'white',
              borderRadius: '16px',
            }}>
              <p style={{ color: colors.textLight }}>לא נמצאו עובדים</p>
            </div>
          )}
        </div>
      )}

      {/* Add Staff Modal */}
      <AnimatePresence>
        {showAddModal && (
          <AddStaffModal
            roleTypes={roleTypes}
            onClose={() => setShowAddModal(false)}
            onAdd={(newStaff) => {
              setStaff(prev => [...prev, newStaff]);
              setShowAddModal(false);
              setSuccessMessage('העובד נוסף בהצלחה');
              setTimeout(() => setSuccessMessage(''), 3000);
            }}
            orgFilter={orgFilter}
          />
        )}
      </AnimatePresence>

      {/* Staff Details Modal */}
      <AnimatePresence>
        {selectedStaff && (
          <StaffDetailsModal
            staff={selectedStaff}
            onClose={() => setSelectedStaff(null)}
            onDelete={handleDelete}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// ==========================================
// Staff Card Component
// ==========================================

function StaffCard({ member, index, onClick }: { member: AdminStaffDto; index: number; onClick: () => void }) {
  const roleColor = roleColors[member.roleTypeId] || { bg: colors.background, fg: colors.textLight };

  // Get unique kindergarten names
  const kindergartenNames = [...new Set(member.dayAssignments.map(d => d.kindergartenName))];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      onClick={onClick}
      style={{
        backgroundColor: 'white',
        borderRadius: '16px',
        padding: '16px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
        cursor: 'pointer',
        transition: 'all 0.2s',
      }}
    >
      <div style={{ display: 'flex', gap: '14px', alignItems: 'center' }}>
        {/* Photo */}
        <div style={{
          width: '48px',
          height: '48px',
          borderRadius: '14px',
          overflow: 'hidden',
          backgroundColor: colors.primaryLight,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}>
          {member.photoUrl ? (
            <img src={member.photoUrl} alt={member.firstName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            <User size={24} color={colors.primary} />
          )}
        </div>

        {/* Info */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
            <h3 style={{ fontSize: '15px', fontWeight: '600', color: colors.text }}>
              {member.firstName} {member.lastName}
            </h3>
            <span style={{
              display: 'inline-block',
              padding: '2px 8px',
              borderRadius: '6px',
              fontSize: '11px',
              fontWeight: '500',
              backgroundColor: roleColor.bg,
              color: roleColor.fg,
            }}>
              {member.roleName}
            </span>
          </div>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            {kindergartenNames.length > 0 && (
              <span style={{ fontSize: '13px', color: colors.textLight, display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Building2 size={14} />
                {kindergartenNames.length === 1
                  ? kindergartenNames[0]
                  : `${kindergartenNames.length} גנים`
                }
              </span>
            )}
            {member.phone && (
              <span style={{ fontSize: '13px', color: colors.textLight, display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Phone size={14} />
                {member.phone}
              </span>
            )}
          </div>
        </div>

        <ChevronLeft size={20} color={colors.textLight} />
      </div>
    </motion.div>
  );
}

// ==========================================
// Staff Details Modal
// ==========================================

function StaffDetailsModal({
  staff,
  onClose,
  onDelete,
}: {
  staff: AdminStaffDto;
  onClose: () => void;
  onDelete: (publicId: string) => void;
}) {
  const roleColor = roleColors[staff.roleTypeId] || { bg: colors.background, fg: colors.textLight };
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
        padding: '16px',
      }}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        style={{
          backgroundColor: 'white',
          borderRadius: '20px',
          padding: '24px',
          width: '100%',
          maxWidth: '420px',
          maxHeight: '80vh',
          overflow: 'auto',
        }}
        dir="rtl"
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
          <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '16px',
              overflow: 'hidden',
              backgroundColor: colors.primaryLight,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              {staff.photoUrl ? (
                <img src={staff.photoUrl} alt={staff.firstName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <User size={28} color={colors.primary} />
              )}
            </div>
            <div>
              <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: colors.text }}>
                {staff.firstName} {staff.lastName}
              </h2>
              <span style={{
                display: 'inline-block',
                padding: '4px 10px',
                borderRadius: '8px',
                fontSize: '12px',
                fontWeight: '500',
                backgroundColor: roleColor.bg,
                color: roleColor.fg,
              }}>
                {staff.roleName}
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '8px',
              backgroundColor: colors.background,
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <X size={18} color={colors.textLight} />
          </button>
        </div>

        {/* Contact Details */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '20px' }}>
          {staff.phone && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Phone size={16} color={colors.textLight} />
              <span style={{ fontSize: '14px', color: colors.text }}>{staff.phone}</span>
            </div>
          )}
          {staff.email && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Mail size={16} color={colors.textLight} />
              <span style={{ fontSize: '14px', color: colors.text }}>{staff.email}</span>
            </div>
          )}
        </div>

        {/* Day Assignments */}
        {staff.dayAssignments.length > 0 && (
          <div style={{ marginBottom: '20px' }}>
            <p style={{ fontSize: '13px', fontWeight: '500', color: colors.text, marginBottom: '10px' }}>שיוך גנים</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {daysOfWeek.map(day => {
                const assignment = staff.dayAssignments.find(a => a.dayOfWeek === day.value);
                return (
                  <div key={day.value} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '8px 12px',
                    borderRadius: '8px',
                    backgroundColor: assignment ? colors.successLight : colors.background,
                  }}>
                    <span style={{
                      fontSize: '13px',
                      fontWeight: '600',
                      color: assignment ? colors.success : colors.textLight,
                      width: '50px',
                    }}>
                      {day.fullLabel}
                    </span>
                    <span style={{ fontSize: '13px', color: assignment ? colors.text : colors.textLight }}>
                      {assignment ? assignment.kindergartenName : 'לא עובד'}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', gap: '8px' }}>
          {!showDeleteConfirm ? (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                padding: '12px',
                backgroundColor: '#fee2e2',
                color: colors.error,
                border: 'none',
                borderRadius: '10px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
              }}
            >
              <Trash2 size={16} />
              מחיקה
            </button>
          ) : (
            <div style={{ flex: 1, display: 'flex', gap: '8px' }}>
              <button
                autoFocus
                onClick={() => setShowDeleteConfirm(false)}
                style={{
                  flex: 1,
                  padding: '12px',
                  backgroundColor: colors.background,
                  color: colors.text,
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '14px',
                  cursor: 'pointer',
                }}
              >
                ביטול
              </button>
              <button
                onClick={() => onDelete(staff.publicId)}
                style={{
                  flex: 1,
                  padding: '12px',
                  backgroundColor: colors.error,
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                }}
              >
                אישור מחיקה
              </button>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

// ==========================================
// Add Staff Modal - Wizard
// ==========================================

function AddStaffModal({
  roleTypes,
  onClose,
  onAdd,
  orgFilter,
}: {
  roleTypes: RoleTypeDto[];
  onClose: () => void;
  onAdd: (staff: AdminStaffDto) => void;
  orgFilter?: string;
}) {
  const [step, setStep] = useState(1);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createdStaff, setCreatedStaff] = useState<AdminStaffDto | null>(null);

  // Step 1: Personal details + role
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [selectedRole, setSelectedRole] = useState(4); // default: teacher
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [photoBase64, setPhotoBase64] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Validations
  const [phoneError, setPhoneError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [usernameStatus, setUsernameStatus] = useState<'idle' | 'checking' | 'available' | 'taken'>('idle');
  const usernameTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isManagementRole = MANAGEMENT_ROLES.includes(selectedRole);

  const validatePhone = (val: string) => {
    const digits = val.replace(/\D/g, '');
    if (digits.length > 0 && digits.length !== 10) {
      setPhoneError('טלפון חייב להכיל 10 ספרות (קידומת + מספר)');
    } else {
      setPhoneError('');
    }
  };

  const validateEmail = (val: string) => {
    if (val && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) {
      setEmailError('כתובת מייל לא תקינה');
    } else {
      setEmailError('');
    }
  };

  const checkUsername = (val: string) => {
    if (usernameTimerRef.current) clearTimeout(usernameTimerRef.current);
    if (!val.trim()) { setUsernameStatus('idle'); return; }
    setUsernameStatus('checking');
    usernameTimerRef.current = setTimeout(async () => {
      try {
        const result = await adminApi.checkUsername(val.trim());
        if (result.success && result.data) {
          setUsernameStatus(result.data.available ? 'available' : 'taken');
        }
      } catch {
        setUsernameStatus('idle');
      }
    }, 500);
  };

  // Step 2: Work days + kindergarten assignment
  const [selectedDays, setSelectedDays] = useState<number[]>([1, 2, 3, 4, 5]);
  const [sameKindergartenForAll, setSameKindergartenForAll] = useState(true);
  const [dayAssignments, setDayAssignments] = useState<Record<number, { publicId: string; name: string } | null>>({});
  const [singleKindergarten, setSingleKindergarten] = useState<{ publicId: string; name: string } | null>(null);

  // Only show staff-relevant roles (category=2 management, category=3)
  const availableRoles = roleTypes.filter(r => r.category === 2 || r.category === 3 || r.id >= 4);

  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      setPhotoPreview(result);
      setPhotoBase64(result.split(',')[1]);
    };
    reader.readAsDataURL(file);
  };

  const toggleDay = (dayValue: number) => {
    setSelectedDays(prev =>
      prev.includes(dayValue)
        ? prev.filter(d => d !== dayValue)
        : [...prev, dayValue].sort()
    );
  };

  const phoneDigits = phone.replace(/\D/g, '');
  const canProceedStep1 = firstName.trim() && lastName.trim() && phone.trim()
    && phoneDigits.length === 10
    && !emailError
    && usernameStatus !== 'taken';
  const canProceedStep2 = selectedDays.length > 0 && (
    sameKindergartenForAll
      ? singleKindergarten !== null
      : selectedDays.every(d => dayAssignments[d] !== null && dayAssignments[d] !== undefined)
  );

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError('');

    try {
      const assignments: { dayOfWeek: number; kindergartenPublicId: string }[] = [];

      // שיבוץ גנים רק לתפקידי גן (גננת/סייעת)
      if (!isManagementRole) {
        if (sameKindergartenForAll && singleKindergarten) {
          selectedDays.forEach(day => {
            assignments.push({ dayOfWeek: day, kindergartenPublicId: singleKindergarten.publicId });
          });
        } else {
          selectedDays.forEach(day => {
            const kg = dayAssignments[day];
            if (kg) {
              assignments.push({ dayOfWeek: day, kindergartenPublicId: kg.publicId });
            }
          });
        }
      }

      const request: CreateStaffRequest = {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phone: phone.trim(),
        email: email.trim() || undefined,
        username: username.trim() || undefined,
        password: password.trim() || undefined,
        roleTypeId: selectedRole,
        dayAssignments: assignments,
        photoBase64: photoBase64 || undefined,
        organizationPublicId: orgFilter || undefined,
      };

      const result = await adminApi.createStaff(request);
      if (result.success && result.data) {
        setCreatedStaff(result.data);
        setStep(4); // step 4 = success summary
      } else {
        setError(result.message || 'שגיאה ביצירת עובד');
      }
    } catch {
      setError('שגיאה ביצירת עובד');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '12px 16px',
    borderRadius: '10px',
    border: `1px solid ${colors.border}`,
    fontSize: '14px',
    outline: 'none',
    direction: 'rtl' as const,
    boxSizing: 'border-box' as const,
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
        padding: '16px',
      }}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        style={{
          backgroundColor: 'white',
          borderRadius: '20px',
          padding: '24px',
          width: '100%',
          maxWidth: '460px',
          maxHeight: '85vh',
          overflow: 'auto',
        }}
        dir="rtl"
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: colors.text }}>
            {step === 1 ? 'פרטים אישיים' : step === 2 ? 'ימי עבודה וגנים' : step === 3 ? 'סיכום' : ''}
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {/* Step indicators */}
            {(isManagementRole ? [1, 3] : [1, 2, 3]).map(s => (
              <div key={s} style={{
                width: '8px',
                height: '8px',
                borderRadius: '4px',
                backgroundColor: s === step ? colors.primary : s < step ? colors.success : colors.border,
              }} />
            ))}
            <button
              onClick={onClose}
              style={{ marginRight: '8px', padding: '6px', backgroundColor: 'transparent', border: 'none', cursor: 'pointer' }}
            >
              <X size={18} color={colors.textLight} />
            </button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 14px',
            backgroundColor: '#fef2f2',
            borderRadius: '8px',
            marginBottom: '16px',
          }}>
            <AlertCircle size={16} color={colors.error} />
            <span style={{ fontSize: '13px', color: colors.error }}>{error}</span>
          </div>
        )}

        {/* Step 1: Personal Details + Role */}
        {step === 1 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            {/* Photo */}
            <div style={{ textAlign: 'center', marginBottom: '8px' }}>
              <div
                onClick={() => fileInputRef.current?.click()}
                style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '20px',
                  margin: '0 auto',
                  backgroundColor: colors.primaryLight,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  overflow: 'hidden',
                  border: `2px dashed ${colors.border}`,
                }}
              >
                {photoPreview ? (
                  <img src={photoPreview} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <Camera size={28} color={colors.textLight} />
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handlePhotoUpload}
                style={{ display: 'none' }}
              />
              <FormLabel text="תמונה" helpText={adminStaffHelpTexts.photo.tooltip} style={{ color: colors.textLight, marginTop: '6px', fontSize: '12px' }} />
            </div>

            {/* Name */}
            <div style={{ display: 'flex', gap: '10px' }}>
              <div style={{ flex: 1 }}>
                <FormLabel text="שם פרטי" required helpText={adminStaffHelpTexts.firstName.tooltip} style={{ color: colors.text }} />
                <input type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} style={inputStyle} />
              </div>
              <div style={{ flex: 1 }}>
                <FormLabel text="שם משפחה" required helpText={adminStaffHelpTexts.lastName.tooltip} style={{ color: colors.text }} />
                <input type="text" value={lastName} onChange={(e) => setLastName(e.target.value)} style={inputStyle} />
              </div>
            </div>

            {/* Phone */}
            <div>
              <FormLabel text="טלפון" required helpText={adminStaffHelpTexts.phone.tooltip} style={{ color: colors.text }} />
              <input type="tel" value={phone} onChange={(e) => { setPhone(e.target.value); validatePhone(e.target.value); }} placeholder="050-000-0000" style={inputStyle} />
              {phoneError && <span style={{ fontSize: '11px', color: colors.error, marginTop: '4px', display: 'block' }}>{phoneError}</span>}
              <HelperText text={adminStaffHelpTexts.phone.helper} />
            </div>

            {/* Email */}
            <div>
              <FormLabel text="אימייל" helpText={adminStaffHelpTexts.email.tooltip} style={{ color: colors.text }} />
              <input type="email" value={email} onChange={(e) => { setEmail(e.target.value); validateEmail(e.target.value); }} style={{ ...inputStyle, direction: 'ltr' as const }} />
              {emailError && <span style={{ fontSize: '11px', color: colors.error, marginTop: '4px', display: 'block' }}>{emailError}</span>}
            </div>

            {/* Username & Password */}
            <div style={{ display: 'flex', gap: '10px' }}>
              <div style={{ flex: 1 }}>
                <FormLabel text="שם משתמש" helpText={adminStaffHelpTexts.username.tooltip} style={{ color: colors.text }} />
                <div style={{ position: 'relative' }}>
                  <input type="text" autoComplete="off" value={username} onChange={(e) => { setUsername(e.target.value); checkUsername(e.target.value); }} style={{ ...inputStyle, direction: 'ltr' as const }} />
                  {usernameStatus === 'checking' && <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '12px', color: colors.textLight }}>...</span>}
                  {usernameStatus === 'available' && <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '14px', color: colors.success }}>✓</span>}
                  {usernameStatus === 'taken' && <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '14px', color: colors.error }}>✗</span>}
                </div>
                {usernameStatus === 'taken' && <span style={{ fontSize: '11px', color: colors.error, marginTop: '4px', display: 'block' }}>שם משתמש תפוס</span>}
                <HelperText text={adminStaffHelpTexts.username.helper} />
              </div>
              <div style={{ flex: 1 }}>
                <FormLabel text="סיסמה" helpText={adminStaffHelpTexts.password.tooltip} style={{ color: colors.text }} />
                <input type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} style={{ ...inputStyle, direction: 'ltr' as const }} />
                <HelperText text={adminStaffHelpTexts.password.helper} />
              </div>
            </div>

            {/* Role Selection */}
            <div>
              <FormLabel text="תפקיד" required helpText={adminStaffHelpTexts.role.tooltip} style={{ color: colors.text }} />
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {availableRoles.map(role => {
                  const rc = roleColors[role.id] || { bg: colors.background, fg: colors.textLight };
                  return (
                    <button
                      key={role.id}
                      onClick={() => setSelectedRole(role.id)}
                      style={{
                        padding: '8px 14px',
                        borderRadius: '10px',
                        border: `2px solid ${selectedRole === role.id ? rc.fg : colors.border}`,
                        backgroundColor: selectedRole === role.id ? rc.bg : 'white',
                        color: selectedRole === role.id ? rc.fg : colors.text,
                        fontSize: '13px',
                        fontWeight: selectedRole === role.id ? '600' : '400',
                        cursor: 'pointer',
                      }}
                    >
                      {role.nameHebrew || role.name}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Next Button */}
            <motion.button
              onClick={() => canProceedStep1 && setStep(isManagementRole ? 3 : 2)}
              disabled={!canProceedStep1}
              style={{
                width: '100%',
                padding: '14px',
                marginTop: '8px',
                borderRadius: '12px',
                border: 'none',
                backgroundColor: canProceedStep1 ? colors.primary : colors.border,
                color: canProceedStep1 ? 'white' : colors.textLight,
                fontSize: '15px',
                fontWeight: 'bold',
                cursor: canProceedStep1 ? 'pointer' : 'not-allowed',
              }}
              whileHover={{ scale: canProceedStep1 ? 1.02 : 1 }}
            >
              המשך
            </motion.button>
          </div>
        )}

        {/* Step 2: Work Days + Kindergarten Assignment */}
        {step === 2 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* Work Days Selection */}
            <div>
              <FormLabel text="ימי עבודה" helpText={adminStaffHelpTexts.dayAssignment.tooltip} style={{ color: colors.text }} />
              <div style={{ display: 'flex', gap: '8px' }}>
                {daysOfWeek.map(day => (
                  <button
                    key={day.value}
                    onClick={() => toggleDay(day.value)}
                    style={{
                      flex: 1,
                      padding: '10px 4px',
                      borderRadius: '10px',
                      border: `2px solid ${selectedDays.includes(day.value) ? colors.success : colors.border}`,
                      backgroundColor: selectedDays.includes(day.value) ? colors.successLight : 'white',
                      color: selectedDays.includes(day.value) ? colors.success : colors.textLight,
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      textAlign: 'center',
                    }}
                  >
                    <div>{day.label}</div>
                    <div style={{ fontSize: '10px', fontWeight: '400' }}>{day.fullLabel}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Toggle: same or different kindergarten per day */}
            <div>
              <FormLabel text="שיוך גנים" helpText={adminStaffHelpTexts.dayAssignment.tooltip} style={{ color: colors.text }} />
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => setSameKindergartenForAll(true)}
                  style={{
                    flex: 1,
                    padding: '10px',
                    borderRadius: '10px',
                    border: `2px solid ${sameKindergartenForAll ? colors.primary : colors.border}`,
                    backgroundColor: sameKindergartenForAll ? colors.primaryLight : 'white',
                    color: sameKindergartenForAll ? colors.primary : colors.text,
                    fontSize: '13px',
                    fontWeight: sameKindergartenForAll ? '600' : '400',
                    cursor: 'pointer',
                  }}
                >
                  גן אחד לכל הימים
                </button>
                <button
                  onClick={() => setSameKindergartenForAll(false)}
                  style={{
                    flex: 1,
                    padding: '10px',
                    borderRadius: '10px',
                    border: `2px solid ${!sameKindergartenForAll ? colors.primary : colors.border}`,
                    backgroundColor: !sameKindergartenForAll ? colors.primaryLight : 'white',
                    color: !sameKindergartenForAll ? colors.primary : colors.text,
                    fontSize: '13px',
                    fontWeight: !sameKindergartenForAll ? '600' : '400',
                    cursor: 'pointer',
                  }}
                >
                  גן שונה לכל יום
                </button>
              </div>
            </div>

            {/* Kindergarten Selection */}
            {sameKindergartenForAll ? (
              <KindergartenSearch
                label="חיפוש גן"
                selected={singleKindergarten}
                onSelect={(kg) => setSingleKindergarten(kg)}
                organizationId={orgFilter}
              />
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {selectedDays.map(dayValue => {
                  const day = daysOfWeek.find(d => d.value === dayValue)!;
                  return (
                    <div key={dayValue}>
                      <KindergartenSearch
                        label={`יום ${day.fullLabel}`}
                        selected={dayAssignments[dayValue] || null}
                        onSelect={(kg) => setDayAssignments(prev => ({ ...prev, [dayValue]: kg }))}
                        organizationId={orgFilter}
                      />
                    </div>
                  );
                })}
              </div>
            )}

            {/* Navigation Buttons */}
            <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
              <button
                onClick={() => setStep(1)}
                style={{
                  flex: 1,
                  padding: '14px',
                  borderRadius: '12px',
                  border: `1px solid ${colors.border}`,
                  backgroundColor: 'white',
                  color: colors.text,
                  fontSize: '15px',
                  cursor: 'pointer',
                }}
              >
                חזרה
              </button>
              <motion.button
                onClick={() => canProceedStep2 && setStep(3)}
                disabled={!canProceedStep2}
                style={{
                  flex: 2,
                  padding: '14px',
                  borderRadius: '12px',
                  border: 'none',
                  backgroundColor: canProceedStep2 ? colors.primary : colors.border,
                  color: canProceedStep2 ? 'white' : colors.textLight,
                  fontSize: '15px',
                  fontWeight: 'bold',
                  cursor: canProceedStep2 ? 'pointer' : 'not-allowed',
                }}
                whileHover={{ scale: canProceedStep2 ? 1.02 : 1 }}
              >
                המשך
              </motion.button>
            </div>
          </div>
        )}

        {/* Step 3: Summary */}
        {step === 3 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* Summary Card */}
            <div style={{
              backgroundColor: colors.background,
              borderRadius: '12px',
              padding: '16px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '14px',
                  overflow: 'hidden',
                  backgroundColor: colors.primaryLight,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  {photoPreview ? (
                    <img src={photoPreview} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <User size={24} color={colors.primary} />
                  )}
                </div>
                <div>
                  <h3 style={{ fontSize: '16px', fontWeight: '600', color: colors.text }}>
                    {firstName} {lastName}
                  </h3>
                  <span style={{
                    padding: '2px 8px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    backgroundColor: (roleColors[selectedRole] || { bg: colors.background }).bg,
                    color: (roleColors[selectedRole] || { fg: colors.textLight }).fg,
                  }}>
                    {availableRoles.find(r => r.id === selectedRole)?.nameHebrew || 'לא ידוע'}
                  </span>
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '13px', color: colors.text }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Phone size={14} color={colors.textLight} /> {phone}
                </div>
                {email && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Mail size={14} color={colors.textLight} /> {email}
                  </div>
                )}
              </div>
            </div>

            {/* Day assignments summary — only for kindergarten roles */}
            {!isManagementRole && (
              <div>
                <p style={{ fontSize: '13px', fontWeight: '500', color: colors.text, marginBottom: '8px' }}>שיוך גנים</p>
                {selectedDays.map(dayValue => {
                  const day = daysOfWeek.find(d => d.value === dayValue)!;
                  const kg = sameKindergartenForAll ? singleKindergarten : dayAssignments[dayValue];
                  return (
                    <div key={dayValue} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '6px 10px',
                      borderRadius: '6px',
                      backgroundColor: colors.successLight,
                      marginBottom: '4px',
                      fontSize: '13px',
                    }}>
                      <span style={{ fontWeight: '600', color: colors.success, width: '50px' }}>
                        {day.fullLabel}
                      </span>
                      <Building2 size={14} color={colors.success} />
                      <span style={{ color: colors.text }}>{kg?.name || '-'}</span>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Navigation Buttons */}
            <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
              <button
                onClick={() => setStep(isManagementRole ? 1 : 2)}
                style={{
                  flex: 1,
                  padding: '14px',
                  borderRadius: '12px',
                  border: `1px solid ${colors.border}`,
                  backgroundColor: 'white',
                  color: colors.text,
                  fontSize: '15px',
                  cursor: 'pointer',
                }}
              >
                חזרה
              </button>
              <motion.button
                onClick={handleSubmit}
                disabled={isSubmitting}
                style={{
                  flex: 2,
                  padding: '14px',
                  borderRadius: '12px',
                  border: 'none',
                  backgroundColor: colors.success,
                  color: 'white',
                  fontSize: '15px',
                  fontWeight: 'bold',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  opacity: isSubmitting ? 0.7 : 1,
                }}
                whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
              >
                {isSubmitting ? 'שומר...' : 'שמור עובד'}
              </motion.button>
            </div>
          </div>
        )}

        {/* Step 4: Success Summary */}
        {step === 4 && createdStaff && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', textAlign: 'center' }}>
            <div style={{
              width: '64px', height: '64px', borderRadius: '50%',
              backgroundColor: colors.successLight, display: 'flex',
              alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 8px',
            }}>
              <CheckCircle2 size={32} color={colors.success} />
            </div>
            <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: colors.text, margin: 0 }}>
              העובד נוצר בהצלחה
            </h3>

            <div style={{
              backgroundColor: colors.background, borderRadius: '12px',
              padding: '16px', textAlign: 'right',
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '14px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: colors.text, fontWeight: '600' }}>{createdStaff.firstName} {createdStaff.lastName}</span>
                  <span style={{ color: colors.textLight }}>שם</span>
                </div>
                {username && (
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: colors.text, fontWeight: '600', direction: 'ltr' }}>{username}</span>
                    <span style={{ color: colors.textLight }}>שם משתמש</span>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: colors.text }}>{phone}</span>
                  <span style={{ color: colors.textLight }}>טלפון</span>
                </div>
              </div>
            </div>

            <p style={{ fontSize: '13px', color: colors.warning, margin: 0 }}>
              העובד יתבקש להחליף סיסמה בכניסה הראשונה למערכת
            </p>

            <motion.button
              onClick={() => { onAdd(createdStaff); }}
              style={{
                width: '100%', padding: '14px', borderRadius: '12px',
                border: 'none', backgroundColor: colors.primary,
                color: 'white', fontSize: '15px', fontWeight: 'bold',
                cursor: 'pointer',
              }}
              whileTap={{ scale: 0.98 }}
            >
              סגור
            </motion.button>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}

// ==========================================
// Kindergarten Search Component
// ==========================================

function KindergartenSearch({
  label,
  selected,
  onSelect,
  organizationId,
}: {
  label: string;
  selected: { publicId: string; name: string } | null;
  onSelect: (kg: { publicId: string; name: string }) => void;
  organizationId?: string;
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<AdminKindergartenSearchDto[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [searching, setSearching] = useState(false);
  const searchTimeout = useRef<ReturnType<typeof setTimeout>>(undefined);

  const doSearch = useCallback(async (searchQuery: string) => {
    setSearching(true);
    try {
      const result = await adminApi.searchKindergartens(searchQuery, organizationId);
      if (result.success && result.data) {
        setResults(result.data);
      }
    } catch {
      setResults([]);
    } finally {
      setSearching(false);
    }
  }, [organizationId]);

  const handleQueryChange = (value: string) => {
    setQuery(value);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => doSearch(value), 300);
    setShowResults(true);
  };

  return (
    <div>
      <label style={{ fontSize: '13px', fontWeight: '500', color: colors.text, display: 'block', marginBottom: '6px' }}>
        {label}
      </label>
      {selected ? (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '10px 14px',
          borderRadius: '10px',
          backgroundColor: colors.successLight,
          border: `1px solid ${colors.success}40`,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Building2 size={16} color={colors.success} />
            <span style={{ fontSize: '14px', color: colors.text }}>{selected.name}</span>
          </div>
          <button
            onClick={() => {
              onSelect(null as any);
              setQuery('');
            }}
            style={{ padding: '4px', backgroundColor: 'transparent', border: 'none', cursor: 'pointer' }}
          >
            <X size={16} color={colors.textLight} />
          </button>
        </div>
      ) : (
        <div style={{ position: 'relative' }}>
          <input
            type="text"
            value={query}
            onChange={(e) => handleQueryChange(e.target.value)}
            onFocus={() => { setShowResults(true); if (!query) doSearch(''); }}
            placeholder="חיפוש גן לפי שם, עיר או רחוב..."
            style={{
              width: '100%',
              padding: '10px 14px',
              borderRadius: '10px',
              border: `1px solid ${colors.border}`,
              fontSize: '14px',
              outline: 'none',
              direction: 'rtl',
              boxSizing: 'border-box',
            }}
          />
          {showResults && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              marginTop: '4px',
              backgroundColor: 'white',
              borderRadius: '10px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
              maxHeight: '180px',
              overflowY: 'auto',
              zIndex: 10,
            }}>
              {searching ? (
                <div style={{ padding: '12px', textAlign: 'center', color: colors.textLight, fontSize: '13px' }}>
                  מחפש...
                </div>
              ) : results.length === 0 ? (
                <div style={{ padding: '12px', textAlign: 'center', color: colors.textLight, fontSize: '13px' }}>
                  לא נמצאו גנים
                </div>
              ) : (
                results.map(kg => (
                  <button
                    key={kg.publicId}
                    onClick={() => {
                      onSelect({ publicId: kg.publicId, name: kg.name });
                      setShowResults(false);
                      setQuery('');
                    }}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      textAlign: 'right',
                      border: 'none',
                      backgroundColor: 'white',
                      cursor: 'pointer',
                      borderBottom: `1px solid ${colors.border}`,
                      fontSize: '13px',
                    }}
                  >
                    <div style={{ fontWeight: '500', color: colors.text }}>{kg.name}</div>
                    <div style={{ fontSize: '12px', color: colors.textLight }}>
                      {kg.city}, {kg.street} {kg.houseNumber || ''}
                    </div>
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
